import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { getModules, assignModules } from "../api/permissionApi";

import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
} from "./ui";

const ModuleSelector = ({
  roleId,
  selectedModules = [],
  refreshRole,
}) => {
  const [modules, setModules] = useState([]);
  const [checked, setChecked] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadModules();
  }, []);

  useEffect(() => {
    setChecked(selectedModules.map((module) => module._id));
  }, [selectedModules]);

  const loadModules = async () => {
    try {
      const data = await getModules();
      setModules(data);
    } catch (error) {
      console.error(error);
    }
  };

  const toggleModule = (id) => {
    if (checked.includes(id)) {
      setChecked(checked.filter((item) => item !== id));
    } else {
      setChecked([...checked, id]);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await assignModules(roleId, checked);
      alert("Modules updated");
      refreshRole();
    } catch (error) {
      alert("Failed to save modules");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border border-neutral-200 dark:border-neutral-800 shadow-none bg-white dark:bg-neutral-900">
      {/* Header – no decorative icon */}
      <CardHeader className="border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-900/50 px-4 sm:px-6 py-4 flex flex-row items-center justify-between gap-4">
        <div>
          <CardTitle className="text-xs font-semibold text-neutral-800 dark:text-neutral-100 uppercase tracking-wider">
            Assigned Modules ({checked.length}/{modules.length})
          </CardTitle>
          <p className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-0.5">
            Select functional areas accessible to this role.
          </p>
        </div>

        <Button
          onClick={handleSave}
          variant="primary"
          isLoading={saving}
          leftIcon={<Save className="w-3.5 h-3.5" />}
          className="text-xs font-semibold px-4 py-2"
        >
          Save Modules
        </Button>
      </CardHeader>

      {/* Grid Content */}
      <CardContent className="p-4 sm:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {modules.map((module) => {
            const isChecked = checked.includes(module._id);
            const displayName =
              module.name ||
              module.displayName ||
              module.moduleName ||
              module.moduleKey;

            return (
              <label
                key={module._id}
                className="flex items-center justify-between p-3.5 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 hover:bg-neutral-50 dark:hover:bg-neutral-800/60 transition-colors cursor-pointer select-none"
              >
                <div className="flex items-center gap-2.5 min-w-0 pr-3">
                  <span className="text-xs font-medium text-neutral-800 dark:text-neutral-100 tracking-tight truncate">
                    {displayName}
                  </span>
                </div>

                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => toggleModule(module._id)}
                  className="sr-only"
                />

                {/* Minimal Toggle Switch */}
                <div
                  className={`relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full transition-colors duration-200 ease-in-out focus:outline-none ${
                    isChecked
                      ? "bg-primary-600 dark:bg-primary-500"
                      : "bg-neutral-200 dark:bg-neutral-700"
                  }`}
                >
                  <span
                    className={`pointer-events-none inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out my-auto ml-0.5 ${
                      isChecked ? "translate-x-4" : "translate-x-0"
                    }`}
                  />
                </div>
              </label>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default ModuleSelector;
